import { Controller } from "@hotwired/stimulus"
import debounce from "lodash/debounce"

export default class extends Controller {
  static targets = [
    "input",
    "hiddenInput",
    "results",
    "appointmentHistoryBadge",
    "firstAppointmentBadge",
    "lastAppointmentBadge",
    "lastAppointmentDate",
    "noShowRateAlert",
    "noShowRateText"
  ]

  connect() {
    // Solo inicializar la funcionalidad de búsqueda si el input no está deshabilitado
    if (!this.inputTarget.disabled) {
      this.search = debounce(this.search.bind(this), 300)
      this.closeResults()
      // Agregar event listener para clicks fuera del componente
      document.addEventListener('click', this.handleClickOutside.bind(this))

      // Add input event listener to handle manual clearing
      this.inputTarget.addEventListener('input', this.handleInputChange.bind(this))
    }

    // Initialize any necessary setup
    this.checkInitialPatient()
  }

  disconnect() {
    // Solo remover event listener si se inicializó
    if (!this.inputTarget.disabled) {
      document.removeEventListener('click', this.handleClickOutside.bind(this))
      this.inputTarget.removeEventListener('input', this.handleInputChange.bind(this))
    }
  }

  handleClickOutside(event) {
    // Verificar si el click fue fuera del input y de los resultados
    if (!this.element.contains(event.target)) {
      this.closeResults()
    }
  }

  handleInputChange(event) {
    const query = event.target.value.trim()

    // If patient field is cleared, reset duration to 30 minutes and clear title
    if (!query || query.length < 2) {
      this.hiddenInputTarget.value = ''
      this.adjustAppointmentDuration(30)
    }
  }

  search() {
    if (this.inputTarget.disabled) return
    const query = this.inputTarget.value.trim()

    // Hide appointment history and no show rate if input is empty or too short
    if (!query || query.length < 2) {
      this.appointmentHistoryBadgeTarget.classList.add('d-none')
      this.hiddenInputTarget.value = ''
      this.resultsTarget.classList.add('d-none')

      // Hide no show rate alert
      const noShowRateAlert = document.querySelector('.no-show-rate-alert')
      if (noShowRateAlert) {
        noShowRateAlert.classList.add('d-none')
      }

      // Reset duration to 30 minutes when patient is cleared
      this.adjustAppointmentDuration(30)
      return
    }

    // Get doctor email from the form
    const doctorEmail = document.querySelector('input[name="doctor_email"]')?.value || ''

    // Fetch search results
    fetch(`/appointments/search_patients?query=${encodeURIComponent(query)}&doctor_email=${encodeURIComponent(doctorEmail)}`)
      .then(response => response.text())
      .then(html => {
        this.resultsTarget.innerHTML = html
        // Asegurar que el dropdown esté por encima del cartel de cancelación
        this.resultsTarget.style.zIndex = "1060"
        this.resultsTarget.classList.remove('d-none')
      })
  }

  selectPatient(event) {
    event.preventDefault()
    const patientId = event.currentTarget.dataset.patientId
    const patientName = event.currentTarget.dataset.patientName

    this.inputTarget.value = patientName
    this.hiddenInputTarget.value = patientId
    this.resultsTarget.classList.add('d-none')

    // Fetch appointment history for selected patient
    this.fetchAppointmentHistory(patientId)

    // Update no show rate
    this.updateNoShowRate(patientId)
  }


  focus() {
    if (this.inputTarget.disabled) return

    // Si el dropdown ya está abierto, cerrarlo
    if (!this.resultsTarget.classList.contains("d-none")) {
      this.closeResults()
      return
    }

    // Si el input está vacío o tiene menos de 2 caracteres, mostrar todos los pacientes
    if (this.inputTarget.value.length < 2) {
      // Get doctor email from the form
      const doctorEmail = document.querySelector('input[name="doctor_email"]')?.value || ''

      fetch(`/appointments/search_patients?doctor_email=${encodeURIComponent(doctorEmail)}`)
        .then(response => response.text())
        .then(html => {
          this.resultsTarget.innerHTML = html
          this.resultsTarget.classList.remove("d-none")
          // Asegurar que el dropdown esté por encima del cartel de cancelación
          this.resultsTarget.style.zIndex = "1060"
        })
    } else {
      // Si tiene 2 o más caracteres, hacer la búsqueda
      this.search()
    }
  }

  closeResults() {
    if (this.hasResultsTarget) {
      this.resultsTarget.classList.add("d-none")
    }
  }

  updateNoShowRate(patientId) {
    fetch(`/appointments/patient_no_show_rate?patient_id=${patientId}`)
      .then(response => response.json())
      .then(data => {
        const noShowRateAlert = this.noShowRateAlertTarget
        const noShowRateText = this.noShowRateTextTarget

        if (data.no_show_rate > 20) {
          noShowRateText.textContent = `Este paciente tiene una tasa de no presentación del ${data.no_show_rate}%`
          noShowRateAlert.style.display = 'block'
        } else {
          noShowRateAlert.style.display = 'none'
        }
      })
  }

  checkInitialPatient() {
    const patientId = this.hiddenInputTarget.value
    if (patientId) {
      this.fetchAppointmentHistory(patientId)
      this.updateNoShowRate(patientId)
    }
  }

  fetchAppointmentHistory(patientId) {
    // Get the start time and date
    let startTime = document.querySelector('[data-appointments-target="startTime"]')?.value;
    let date = document.querySelector('[data-appointments-target="dateField"]')?.value;

    let params = new URLSearchParams({
      patient_id: patientId
    });

    // Add new appointment time if we're creating a new appointment
    if (startTime && date) {
      params.append('new_appointment_time', `${date} ${startTime}`);
    }

    fetch(`/appointments/patient_appointment_history?${params}`)
      .then(response => response.json())
      .then(data => {
        const appointmentHistoryBadge = this.appointmentHistoryBadgeTarget
        const firstAppointmentBadge = this.firstAppointmentBadgeTarget
        const lastAppointmentBadge = this.lastAppointmentBadgeTarget
        const lastAppointmentDate = this.lastAppointmentDateTarget

        appointmentHistoryBadge.classList.remove('d-none')

        if (data.is_first_appointment) {
          firstAppointmentBadge.classList.remove('d-none')
          lastAppointmentBadge.classList.add('d-none')

          // Adjust duration to 45 minutes for first appointment
          this.adjustAppointmentDuration(45)
        } else {
          firstAppointmentBadge.classList.add('d-none')
          lastAppointmentBadge.classList.remove('d-none')

          // Adjust duration to 30 minutes for returning patients
          this.adjustAppointmentDuration(30)

          // Format the date with days ago
          const daysAgoText = data.latest_appointment.days_ago === 0 ? 'hoy' :
                           data.latest_appointment.days_ago === 1 ? 'ayer' :
                           `hace ${data.latest_appointment.days_ago} días`

          lastAppointmentDate.textContent =
            `${data.latest_appointment.date} ${data.latest_appointment.time} (${daysAgoText})`
        }
      })
  }

  adjustAppointmentDuration(durationMinutes) {
    const appointmentsController = this.application.getControllerForElementAndIdentifier(
      this.element.closest('[data-controller="appointments"]'),
      'appointments'
    )

    if (appointmentsController && appointmentsController.adjustEndTime) {
      appointmentsController.adjustEndTime(durationMinutes)
    }
  }
}
